import { useEffect, useState } from "react";
import { Mic, Heart, ShieldCheck, Smile, BookHeart, HandHeart, Timer, Check } from "lucide-react";
import { useT } from "@/i18n";
import SpeakButton from "@/components/SpeakButton";

const LESSONS = [
  { icon: Mic, color: "bg-orange-500", es: ["Habla sin pena", "Todos sentimos nervios. Respira hondo, sonríe y habla despacio. ¡Cada vez será más fácil!"], en: ["Speak without fear", "Everyone gets nervous. Breathe deeply, smile and speak slowly. It gets easier every time!"] },
  { icon: ShieldCheck, color: "bg-green-500", es: ["Di siempre la verdad", "Cuenta cómo es tu producto de verdad. Nunca prometas algo que no es. La honestidad hace que la gente confíe en ti."], en: ["Always tell the truth", "Say what your product is really like. Never promise something it isn't. Honesty makes people trust you."] },
  { icon: Heart, color: "bg-pink-500", es: ["Enamórate de tu producto", "Si a ti te encanta lo que vendes, se nota. Conoce cada detalle y por qué es especial."], en: ["Love your product", "If you love what you sell, people notice. Know every detail and why it's special."] },
  { icon: BookHeart, color: "bg-violet-500", es: ["Cuenta tu historia", "Di por qué lo hiciste y cómo lo preparaste. A la gente le gustan las historias reales."], en: ["Tell your story", "Say why you made it and how you prepared it. People love real stories."] },
  { icon: Smile, color: "bg-sky-500", es: ["Mira a los ojos y escucha", "Saluda, mira a la persona y escucha lo que necesita. Vender también es ayudar."], en: ["Look and listen", "Say hello, look at the person and listen to what they need. Selling is also helping."] },
  { icon: HandHeart, color: "bg-amber-500", es: ["Da las gracias", "Aunque no te compren, di «¡Gracias por escucharme!». Ser amable siempre suma."], en: ["Say thank you", "Even if they don't buy, say “Thanks for listening!”. Being kind always helps."] },
];

const FIELDS = {
  es: [["name", "Me llamo..."], ["product", "Vendo... (ej: galletas de avena)"], ["how", "Lo hice con... (ej: mi abuela, ingredientes frescos)"], ["why", "Me encanta porque..."], ["price", "Cuesta... (ej: 1 dólar)"]],
  en: [["name", "My name is..."], ["product", "I sell... (e.g. oatmeal cookies)"], ["how", "I made it with... (e.g. my grandma, fresh ingredients)"], ["why", "I love it because..."], ["price", "It costs... (e.g. 1 dollar)"]],
};

const CHECKS = {
  es: ["Sonreí y saludé", "Hablé despacio y fuerte", "Miré a los ojos", "Dije la verdad sobre mi producto", "Di las gracias"],
  en: ["I smiled and said hello", "I spoke slowly and clearly", "I looked them in the eyes", "I told the truth about my product", "I said thank you"],
};

const TX = {
  es: { title: "Cómo vender con honestidad", sub: "Lumi te enseña a presentar tu negocio con confianza y siempre diciendo la verdad.", pitch: "Arma tu presentación de venta", yours: "Tu presentación:", practice: "Practica en voz alta (30 segundos)", start: "¡Empezar a practicar!", again: "Practicar otra vez", left: "segundos", check: "¿Cómo te fue?", great: "¡Eres un gran vendedor honesto! Lumi está orgullosa de ti." },
  en: { title: "How to sell honestly", sub: "Lumi teaches you to present your business with confidence and always telling the truth.", pitch: "Build your sales pitch", yours: "Your pitch:", practice: "Practice out loud (30 seconds)", start: "Start practicing!", again: "Practice again", left: "seconds", check: "How did it go?", great: "You're a great honest seller! Lumi is proud of you." },
};

const buildPitch = (p, lang) => {
  if (!p.product) return "";
  const es = lang !== "en";
  return [
    p.name && (es ? `¡Hola! Me llamo ${p.name}.` : `Hi! My name is ${p.name}.`),
    es ? `Vendo ${p.product}.` : `I sell ${p.product}.`,
    p.how && (es ? `Lo hice con ${p.how}.` : `I made it with ${p.how}.`),
    p.why && (es ? `Me encanta porque ${p.why}.` : `I love it because ${p.why}.`),
    p.price && (es ? `Cuesta ${p.price}.` : `It costs ${p.price}.`),
    es ? "¡Gracias por escucharme!" : "Thanks for listening!",
  ].filter(Boolean).join(" ");
};

export default function SellHonestly() {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [p, setP] = useState({});
  const [time, setTime] = useState(null);
  const [done, setDone] = useState({});
  const pitch = buildPitch(p, lang);
  const checks = CHECKS[lang] || CHECKS.es;
  const allDone = checks.every((_, i) => done[i]);

  useEffect(() => {
    if (time === null || time <= 0) return;
    const t = setTimeout(() => setTime((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [time]);

  return (
    <section className="learn-bg rounded-[2rem] border-4 border-white shadow-xl p-5 sm:p-8 mt-8 space-y-6" data-testid="sell-honestly">
      <div className="flex items-center gap-4">
        <img src="/img/lumi.webp" alt="" className="w-20 sm:w-24 shrink-0 mascot-wiggle" />
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-fredoka text-2xl sm:text-4xl font-bold bubble-text text-white">{tx.title}</h2>
            <SpeakButton compact mascot="lumi" text={`${tx.title}. ${tx.sub}`} testId="sell-title-speak" />
          </div>
          <p className="mt-1 inline-block bg-white/90 rounded-full px-4 py-1 font-bold text-blue-900 text-sm">{tx.sub}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {LESSONS.map((l, i) => {
          const Icon = l.icon; const [t, d] = l[lang] || l.es;
          return (
            <div key={i} className="bg-white rounded-3xl p-4 shadow-md" data-testid={`sell-lesson-${i}`}>
              <div className="flex items-center gap-2">
                <span className={`${l.color} rounded-2xl p-2`}><Icon className="w-5 h-5 text-white" /></span>
                <p className="font-fredoka text-lg font-bold text-blue-950 flex-1">{t}</p>
                <SpeakButton compact mascot="lumi" text={`${t}. ${d}`} testId={`sell-lesson-speak-${i}`} />
              </div>
              <p className="mt-2 text-sm font-semibold text-blue-900">{d}</p>
            </div>
          );
        })}
      </div>
      <div className="bg-white/80 rounded-[2rem] p-4 sm:p-6 space-y-4">
        <h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.pitch}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {(FIELDS[lang] || FIELDS.es).map(([k, ph]) => (
            <input key={k} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value.slice(0, 80) })} placeholder={ph}
              className="w-full bg-white border-4 border-sky-100 rounded-2xl px-4 py-2.5 font-semibold focus:outline-none focus:border-sky-300" data-testid={`pitch-${k}`} />
          ))}
        </div>
        {pitch && (
          <div className="bg-yellow-100 rounded-2xl px-4 py-3 flex flex-wrap items-start gap-2" data-testid="pitch-result">
            <p className="font-bold text-blue-950 flex-1"><span className="text-blue-700">{tx.yours}</span> {pitch}</p>
            <SpeakButton key={pitch} mascot="lumi" text={pitch} testId="pitch-speak" />
          </div>
        )}
        {pitch && (
          <div className="space-y-3">
            <p className="font-fredoka text-lg font-bold text-blue-950 inline-flex items-center gap-2"><Timer className="w-5 h-5" /> {tx.practice}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => { setTime(30); setDone({}); }} className="glossy-btn bg-yellow-300 text-blue-900 font-fredoka font-bold text-lg px-6 py-2.5 rounded-full" data-testid="pitch-practice-btn">
                {time === 0 ? tx.again : tx.start}
              </button>
              {time !== null && <span className="font-fredoka text-3xl font-bold text-blue-950" data-testid="pitch-timer">{time} {tx.left}</span>}
            </div>
            {time === 0 && (
              <div className="bg-white rounded-2xl p-4 shadow space-y-2" data-testid="pitch-checklist">
                <p className="font-bold text-blue-950">{tx.check}</p>
                {checks.map((c, i) => (
                  <button key={i} type="button" onClick={() => setDone({ ...done, [i]: !done[i] })} className="flex items-center gap-2 font-semibold text-blue-900" data-testid={`pitch-check-${i}`}>
                    <span className={`w-6 h-6 rounded-lg border-4 flex items-center justify-center ${done[i] ? "bg-green-500 border-green-500" : "border-sky-200"}`}>{done[i] && <Check className="w-4 h-4 text-white" />}</span>{c}
                  </button>
                ))}
                {allDone && <p className="mt-2 bg-green-100 rounded-2xl px-4 py-2 font-fredoka text-lg font-bold text-green-800" data-testid="pitch-great">{tx.great}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
